import React, { useLayoutEffect, useRef, useState } from "react";
import Input from "../../BaseInput";
import "./flexibleSelect.less";
import FlexibleSelectList from "./FlexibleSelectList";
import { FlexibleSelectData, FlexibleSelectProps } from "./type";
import { Dropdown, Input as AntInput } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useUpdateEffect } from "ahooks";

const FlexibleSelect: React.FC<FlexibleSelectProps> = function (props) {
    const {
        data,
        inputTextMap,
        onChange,
        onFocus,
        onSearch,
        inputValue,
        singleValue,
        multipleValue,
        onClear,
        shopId,
        itemHeight,
        className,
        type,

        defaultMultiple,
        onMultipleChange,

        needSimpleTitle,
        onSimpleTitleChange,
        enabledTopSellingProductPrinting,
        onEnabledTopSellingProductPrintingChange,
        needSkuSimpleTitle,
        onSkuSimpleTitleChange,

        onSelectAllChange,
        isIndeterminate,
        isSelectAll,

        searchText,

        showTitlePic,
        onShowTitlePicChange,
    } = props;
    /** 是否开启下拉 */
    const [visible, updateVisible] = useState(false);
    const inputRef = useRef<AntInput>();
    const isComposingRef = useRef(false);
    const compositionEndValueRef = useRef<string | null>(null);
    const [composingValue, updateComposingValue] = useState("");
    const inputDisplayValue = singleValue ? data.find(({ value }) => value === singleValue)?.inputText : inputValue;
    const [inputDraftValue, updateInputDraftValue] = useState(inputDisplayValue || "");
    const [multiple, updateMultiple] = useState(defaultMultiple || false);
    /** 输入过程中，数据源会发生变化，此时禁止重新计算宽度 */
    const preventComputedTimerRef = useRef<NodeJS.Timeout>(undefined);
    const preventComputed = !!preventComputedTimerRef.current;

    useUpdateEffect(() => {
        if (onMultipleChange) {
            onMultipleChange(multiple);
        }
    }, [multiple]);

    /** 输入时表单值存在一次渲染延迟，使用草稿值避免受控 Input 回填旧值后将光标移到末尾 */
    useLayoutEffect(() => {
        updateInputDraftValue(inputDisplayValue || "");
    }, [inputDisplayValue]);

    function handlePreventComputed() {
        if (preventComputedTimerRef.current) {
            clearTimeout(preventComputedTimerRef.current);
        }

        preventComputedTimerRef.current = setTimeout(() => {
            preventComputedTimerRef.current = null;
        }, 3);
    }

    function handleFocus() {
        onFocus?.();

        // 当下拉处于打开状态时，如果点击输入框，需要保持下拉菜单的打开状态。
        // 但由于点击输入框会触发 dropdown 的 onOpenChange(false) 方法，这里需要通过延时操作重新打开下拉菜单。
        setTimeout(() => {
            updateVisible(true);
        });
    }

    function triggerInputChange(value: string) {
        onChange(value, null, null);

        if (onSearch) {
            onSearch(value);
        }

        handlePreventComputed();
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;

        if (isComposingRef.current) {
            updateComposingValue(value);
            return;
        }

        /** 部分浏览器会在 compositionend 后再次触发 change，需要避免重复提交 */
        if (compositionEndValueRef.current === value) {
            compositionEndValueRef.current = null;
            return;
        }

        compositionEndValueRef.current = null;
        updateInputDraftValue(value);
        triggerInputChange(value);
    }

    function handleCompositionStart(e: React.CompositionEvent<HTMLInputElement>) {
        isComposingRef.current = true;
        compositionEndValueRef.current = null;
        updateComposingValue(e.currentTarget.value);
    }

    function handleCompositionEnd(e: React.CompositionEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;

        isComposingRef.current = false;
        compositionEndValueRef.current = value;
        updateComposingValue("");
        updateInputDraftValue(value);
        triggerInputChange(value);
    }

    function handleSelectChange(data: FlexibleSelectData, checked: boolean) {
        const prevValues = multipleValue || [];
        const nextMultipleValues = [...prevValues];

        if (!multiple) {
            updateVisible(false);

            onChange(data.inputText, data.value, null);

            inputRef.current?.focus();
        } else {
            if (checked) {
                nextMultipleValues.push(data.value);
            } else {
                const removeIndex = nextMultipleValues.findIndex((value) => value === data.value);

                if (removeIndex > -1) {
                    nextMultipleValues.splice(removeIndex, 1);
                }
            }

            onChange("", null, nextMultipleValues);
        }
    }

    function handleClear(e: React.MouseEvent<HTMLSpanElement>) {
        e.stopPropagation();

        if (onClear) {
            onClear();
        }
    }

    /** 1.如果是单选模式，直接使用输入框的值 */
    /** 2.如果为多选模式，商品名称输入框仅展示已勾选的商品名称(商品名称显示第一个名称前6个字，后续多选择商品+1，+2，+3这样显示），不能再输入 */
    function buildMultipleResult() {
        if (!multipleValue?.length) return "";

        let firstItemText = inputTextMap.get(multipleValue[0]);

        if (!firstItemText) return "";

        firstItemText = firstItemText.length > 3 ? firstItemText.slice(0, 3) + "..." : firstItemText;

        const restText = multipleValue.length >= 2 ? `+${multipleValue.length - 1}` : null;

        return (
            <>
                <span>{firstItemText}</span>

                {restText && <span className="ml5 tag">{restText}</span>}

                <span className="close" onClick={handleClear}>
                    <CloseCircleOutlined />
                </span>
            </>
        );
    }

    return (
        <Dropdown menu={{ items: [] }}
            rootClassName="link-flexible-select"
            trigger={["click"]}
            onOpenChange={updateVisible}
            open={visible}
            popupRender={() => <FlexibleSelectList
                    multiple={multiple}
                    onMultipleChange={updateMultiple}
                    onSelectAllChange={onSelectAllChange}
                    isIndeterminate={isIndeterminate}
                    isSelectAll={isSelectAll}
                    type={type}
                    className={className}
                    data={data}
                    onOpenChange={updateVisible}
                    onChange={handleSelectChange}
                    searchText={searchText}
                    onSearch={onSearch}
                    multipleValue={multipleValue}
                    shopId={shopId}
                    itemHeight={itemHeight}
                    preventComputed={preventComputed}
                    onPreventComputed={handlePreventComputed}
                    needSimpleTitle={needSimpleTitle}
                    onSimpleTitleChange={onSimpleTitleChange}
                    enabledTopSellingProductPrinting={enabledTopSellingProductPrinting}
                    onEnabledTopSellingProductPrintingChange={onEnabledTopSellingProductPrintingChange}
                    needSkuSimpleTitle={needSkuSimpleTitle}
                    onSkuSimpleTitleChange={onSkuSimpleTitleChange}
                    showTitlePic={showTitlePic}
                    onShowTitlePicChange={onShowTitlePicChange}
                />}
        >
            {multiple ? (
                <div className="link-flexible-select-result" tabIndex={1} onClick={handleFocus}>
                    {buildMultipleResult()}
                </div>
            ) : (
                <Input
                    className="input"
                    value={isComposingRef.current ? composingValue : inputDraftValue}
                    onChange={handleInputChange}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    ref={inputRef}
                    onClick={handleFocus}
                />
            )}
        </Dropdown>
    );
};

export default React.memo(FlexibleSelect);
